import { getCurrentDateInUnixTimeStamp } from "../../../../shared";
import { globalConstants, inboxReduxConstants } from "../../constants";

const { SUCCESS, FAILURE } = globalConstants;
const {
  SEND_MESSAGE,
  GET_SURVEY_USERS,
  RESET_AI_RESPONSE,
  RESET_CONVERSATION,
  SAVE_INTERNAL_NOTES,
  GENERATE_AI_RESPONSE,
  GET_CUSTOMER_INSIGHTS,
  GET_ALL_CONVERSATIONS,
  RESET_ALL_CONVERSATIONS,
  UPDATE_ALL_CONVERSATIONS,
  GET_CONVERSATION_MESSAGES,
  GET_PREVIEW_CUSTOMER_SURVEY,
  GET_PREVIOUS_INTERNAL_NOTES,
  GET_CONVERSATION_ORDER_DETAILS,
  GET_PREVIOUS_CONVERSATION_DATES,
  RESET_PREVIOUS_CONVERSATION_DATES,
  UPDATE_CUSTOMER_CONVERSATION_MESSAGES,
} = inboxReduxConstants;

const initialState = {
  storeData: {},
  surveyUsers: [],
  allConversations: [],
  conversation: {
    customerId: 0,
    customerMessages: [],
    customerExternalId: 0,
    hasInternalNotes: false,
    previousInternalNotes: [],
    customerConversationId: 0,
    customerInsights: {},
    orderDetails: {},
  },
  customerPreviewSurvey: {},
  previousConversationDates: [],
  aiResponse: null,
};

const sortDataUsingId = (data) => {
  return data.sort((a, b) => a.id - b.id);
};

/*Helper function to check if the current logged user id is in assigned from ids array
and check if customer conversation id's includes the params id*/
const checkUserAndIdMatch = (id, convId) => {
  return Number(id) === Number(convId);
};

//Sort and move updated conversation to top
const sortByUpdatedConversation = (state, data) => {
  const { customerConversationIds, message } = data;
  const updatedConversation = state.allConversations.map((conv) => {
    return {
      ...conv,
      ...(checkUserAndIdMatch(conv.id, customerConversationIds[0]) && {
        message: message,
        lastCommunication: getCurrentDateInUnixTimeStamp(),
      }),
    };
  });

  return updatedConversation.sort((a, b) => {
    //Moving the updated chat to the top.
    return (
      checkUserAndIdMatch(b.id, customerConversationIds[0]) -
      checkUserAndIdMatch(a.id, customerConversationIds[0])
    );
  });
};

//Update conversation when receive message from signlR
const updateOverConversationAndMessages = (state, action) => {
  const data = action.data;
  const { customerConversationIds } = data;

  const isCurrentChatOpen =
    checkUserAndIdMatch(
      state.conversation?.customerConversationId,
      customerConversationIds[0]
    ) ||
    checkUserAndIdMatch(
      state.conversation.customerMessages[0]?.customerConversationIds[0],
      customerConversationIds[0]
    );

  if (isCurrentChatOpen) {
    return {
      ...state,
      conversation: {
        ...state.conversation,
        customerMessages: [...state.conversation.customerMessages, data],
      },
      allConversations: sortByUpdatedConversation(state, data),
    };
  } else {
    return {
      ...state,
      allConversations: state.allConversations
        .map((conv) => {
          if (checkUserAndIdMatch(conv.id, customerConversationIds[0])) {
            return {
              ...conv,
              message: data.message,
              lastCommunication: getCurrentDateInUnixTimeStamp(),
              ...((data?.inReplyTo || data?.systemUserId === 0) && {
                hasUnreadMessages: true,
              }),
            };
          }
          return conv;
        })
        .sort((a, b) => {
          //Moving the updated chat to the top.
          return (
            checkUserAndIdMatch(b.id, customerConversationIds[0]) -
            checkUserAndIdMatch(a.id, customerConversationIds[0])
          );
        }),
    };
  }
};

const updateAllConversationMessagesIncludeOpenChat = (state, { data }) => {
  return {
    ...state,
    allConversations: [...state.allConversations, ...data],
  };
};

const updateAllConversations = (state, newConversation) => {
  const { allConversations, conversation } = state;
  const isChatExist = allConversations.some((conv) => {
    return conv.customerId === newConversation.customerId;
  });
  const isStoreRelevantConversations = allConversations.some(
    (conv) => conv.conversationStoreId === newConversation.conversationStoreId
  );

  if (
    conversation.customerId === newConversation.customerId ||
    !isStoreRelevantConversations
  ) {
    return allConversations;
  } else if (
    isChatExist &&
    conversation.customerId !== newConversation.customerId
  ) {
    //Remove the existing conversation and add the updated one to the start
    const filteredConversations = allConversations.filter(
      (conv) => conv.customerId !== newConversation.customerId
    );
    return [newConversation, ...filteredConversations];
  } else {
    //Remove last conversation in array and add new, due to infinite scroll.
    const updatedConversations = [newConversation, ...allConversations];
    if (
      updateAllConversations.length > 15 &&
      updatedConversations.length % 15 !== 0
    ) {
      updatedConversations.pop();
    }
    return updatedConversations;
  }
};

export const inboxReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_ALL_CONVERSATIONS + SUCCESS:
      return updateAllConversationMessagesIncludeOpenChat(state, action.data);
    case RESET_ALL_CONVERSATIONS + SUCCESS:
      return {
        ...state,
        allConversations: action.data,
      };
    case GET_CONVERSATION_MESSAGES + SUCCESS:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          conversationStatus:
            action.data.conversationInfoDto.conversationStatus,
          customerMessages: sortDataUsingId(
            action.data.conversationInfoDto.customerMessages
          ),
          hasInternalNotes: action.data.conversationInfoDto.hasInternalNotes,
          customerExternalId:
            action.data.conversationInfoDto.customerExternalId,
          customerConversationId: action.data.conversationId,
          customerId: action.data.customerId,
          autoResponderStatus:
            action.data.conversationInfoDto?.autoResponderStatus,
        },
        storeData: {
          storeId: action.data?.conversationInfoDto?.storeId,
          storeExternalId: action.data?.conversationInfoDto?.storeExternalId,
        },
      };
    case GET_PREVIOUS_CONVERSATION_DATES + SUCCESS:
      return {
        ...state,
        previousConversationDates: [
          ...state.previousConversationDates,
          ...action.data.previousDates,
        ],
      };
    case RESET_PREVIOUS_CONVERSATION_DATES + SUCCESS:
      return {
        ...state,
        previousConversationDates: [],
      };
    case SEND_MESSAGE + SUCCESS:
      return updateOverConversationAndMessages(state, action);
    case GET_PREVIEW_CUSTOMER_SURVEY + SUCCESS:
      return {
        ...state,
        customerPreviewSurvey: action.data.previewSurveyDto,
      };
    case UPDATE_CUSTOMER_CONVERSATION_MESSAGES + SUCCESS:
      return {
        ...state,
        allConversations: state.allConversations.map((conv) => {
          return {
            ...conv,
            ...(conv.id === action.data && {
              hasUnreadMessages: false,
              isNew: false,
            }),
          };
        }),
      };
    case GET_SURVEY_USERS + SUCCESS:
      return {
        ...state,
        surveyUsers: action.data.users,
      };
    case SAVE_INTERNAL_NOTES + SUCCESS:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          hasInternalNotes: true,
        },
      };
    case GET_PREVIOUS_INTERNAL_NOTES + SUCCESS:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          previousInternalNotes: action.data.data.sort((a, b) => b.id - a.id),
        },
      };
    case GET_CUSTOMER_INSIGHTS + SUCCESS:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          customerInsights: action.data,
        },
      };
    case GET_CUSTOMER_INSIGHTS + FAILURE:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          customerInsights: {},
        },
      };
    case RESET_CONVERSATION + SUCCESS:
      return {
        ...state,
        conversation: initialState.conversation,
      };
    case UPDATE_ALL_CONVERSATIONS + SUCCESS:
      return {
        ...state,
        allConversations: updateAllConversations(state, action.data),
      };
    case GENERATE_AI_RESPONSE + SUCCESS:
      return {
        ...state,
        aiResponse: action.data?.azureAIGenratedResponse,
      };
    case GENERATE_AI_RESPONSE + FAILURE:
    case RESET_AI_RESPONSE + SUCCESS:
      return {
        ...state,
        aiResponse: null,
      };
    case GET_CONVERSATION_ORDER_DETAILS + SUCCESS:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          orderDetails: action.data?.details,
        },
      };
    case GET_CONVERSATION_ORDER_DETAILS + FAILURE:
      return {
        ...state,
        conversation: {
          ...state.conversation,
          orderDetails: {},
        },
      };
    default:
      return state;
  }
};
